import { ref, computed, onMounted, onUnmounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useAiMetadataStore } from '../store/useAiMetadataStore';
import { useViewerItemStore } from '../store/useViewerItemStore';
import { TrackEvents } from '@/composables/useTrack';

export interface Chapter {
  title: string;
  timecode?: string;
  startTime: number;
  endTime?: number;
}

/**
 * Shared composable for chapter navigation and video time tracking.
 * Used by both ChaptersTab (owner) and GuestChaptersSidePanel (guest).
 */
export const useChapters = () => {
  const itemStore = useViewerItemStore();
  const aiMetadataStore = useAiMetadataStore();

  const { hasChapters, chapters } = storeToRefs(aiMetadataStore);

  const currentTime = ref(0);
  let videoElement: HTMLVideoElement | null = null;
  let observer: MutationObserver | null = null;

  const updateCurrentTime = () => {
    if (videoElement) {
      currentTime.value = videoElement.currentTime;
    }
  };

  const attachVideoListener = (video: HTMLVideoElement) => {
    if (videoElement === video) {
      return;
    }
    detachVideoListener();
    videoElement = video;
    video.addEventListener('timeupdate', updateCurrentTime);
  };

  const detachVideoListener = () => {
    if (videoElement) {
      videoElement.removeEventListener('timeupdate', updateCurrentTime);
      videoElement = null;
    }
  };

  const findAndAttachVideo = () => {
    const video = document.querySelector('video');
    if (video) {
      attachVideoListener(video);
    }
  };

  onMounted(() => {
    findAndAttachVideo();

    // Use MutationObserver to detect when video element is added dynamically
    observer = new MutationObserver(() => {
      if (!videoElement) {
        findAndAttachVideo();
      }
    });
    const viewerRoot = document.getElementById('viewer-content') || document.getElementById('main') || document.body;
    observer.observe(viewerRoot, { childList: true, subtree: true });
  });

  onUnmounted(() => {
    detachVideoListener();
    if (observer) {
      observer.disconnect();
      observer = null;
    }
  });

  const isActiveChapter = (chapter: Chapter): boolean => {
    const chaptersList = chapters?.value ?? [];
    if (chaptersList.length === 0) {
      return false;
    }

    const chapterIndex = chaptersList.findIndex((c: Chapter) => c.startTime === chapter.startTime);
    const nextChapter = chaptersList[chapterIndex + 1];

    if (nextChapter) {
      return currentTime.value >= chapter.startTime && currentTime.value < nextChapter.startTime;
    }

    return currentTime.value >= chapter.startTime;
  };

  const activeChapter = computed((): Chapter | null => {
    const chaptersList = chapters?.value ?? [];
    for (let i = chaptersList.length - 1; i >= 0; i--) {
      if (currentTime.value >= chaptersList[i].startTime) {
        return chaptersList[i];
      }
    }
    return chaptersList[0] || null;
  });

  const formatTimeCode = (timecode?: string): string => {
    if (timecode && timecode.startsWith('00:')) {
      return timecode.slice(3);
    }
    return timecode || '0:00';
  };

  const seekToChapter = (chapter: Chapter) => {
    itemStore.setVideoTime(chapter.startTime);
  };

  const getChapterShareConfig = (chapter: Chapter) => ({
    icon: 'general.copy',
    tooltip: 'Copy chapter link',
    dataTestId: `chapter-share-${chapter.startTime}`,
    content: itemStore.getTimestampedShareLink(chapter.startTime),
    successMessage: 'Chapter link copied to clipboard',
    trackingEvent: TrackEvents.VIEWER_CHAPTER_SHARE,
  });

  return {
    hasChapters,
    chapters,
    currentTime,
    isActiveChapter,
    activeChapter,
    formatTimeCode,
    seekToChapter,
    getChapterShareConfig,
  };
};
